// Hook: Mutations for task actions (start, submit, approve, reject, close, handoff)
// Run `yarn codegen` after adding mutations.federation.gql to generate typed hooks.

import {
  useStartTaskMutation,
  useSubmitTaskForReviewMutation,
  useApproveTaskMutation,
  useRejectTaskMutation,
  useResolveTaskMutation,
  useHandoffTaskToExternalPartyMutation,
  useRecordExternalApprovalOnBehalfMutation,
  useReassignTaskMutation,
  useClaimTaskMutation,
  useCancelTaskAssignmentMutation,
  GetWorkflowDocument,
} from "#graphql/supergraphHooks";
import { onApiError } from "../utils/apiErrorToast";
import type { ClaimTaskMutation, CancelTaskAssignmentMutation } from "#graphql/supergraphTypes";
import type { ApolloCache } from "@apollo/client";

type AssignmentMutationTaskFragment = NonNullable<
  ClaimTaskMutation["claimTask"] | CancelTaskAssignmentMutation["cancelTaskAssignment"]
>;

// Payload shapes for actions that need extra data
interface NotePayload {
  note?: string;
}

// Approve carries an optional next-step assignee picked from the confirm-submit
// modal's GP user dropdown. BE atomically approves the current task and
// assigns the next active step to this user.
interface ApprovePayload {
  note?: string;
  nextAssigneeId?: number;
}

interface ApproveOnBehalfPayload {
  note: string;
  evidenceFileKeys?: string[];
}

interface RejectPayload {
  note?: string;
  // Structured reason captured when returning an external task to the fund admin.
  rejectionReason?: string;
}

interface ReassignPayload {
  newAssigneeId: string;
  newAssigneeDisplayName?: string;
  note?: string;
}

interface ClaimPayload {
  displayName?: string;
}

interface UseTaskActionsResult {
  performAction: (
    taskStepId: number,
    action: string,
    payload?: Record<string, unknown>,
  ) => Promise<void>;
  loading: boolean;
  error: Error | null;
}

export function useTaskActions(workflowId?: string): UseTaskActionsResult {
  // Provide refetchQueries so the workflow detail re-loads after any mutation.
  // Guard against non-numeric workflowId — Number("abc") would refetch with id: NaN.
  const parsedId = workflowId ? Number(workflowId) : NaN;
  const refetchOptions = {
    ...(Number.isFinite(parsedId)
      ? {
          refetchQueries: [
            {
              query: GetWorkflowDocument,
              variables: { id: parsedId },
            },
          ],
        }
      : {}),
    onError: onApiError,
  };

  const [startTask, startResult] = useStartTaskMutation(refetchOptions);
  const [submitTaskForReview, submitResult] = useSubmitTaskForReviewMutation(refetchOptions);
  const [approveTask, approveResult] = useApproveTaskMutation(refetchOptions);
  const [rejectTask, rejectResult] = useRejectTaskMutation(refetchOptions);
  const [resolveTask, resolveResult] = useResolveTaskMutation(refetchOptions);
  const [handoffTaskToExternalParty, handoffResult] =
    useHandoffTaskToExternalPartyMutation(refetchOptions);
  const [recordExternalApprovalOnBehalf, approveOnBehalfResult] =
    useRecordExternalApprovalOnBehalfMutation(refetchOptions);
  const [reassignTask, reassignResult] = useReassignTaskMutation(refetchOptions);
  // Apollo's auto-merge of a mutation response into the cache doesn't always
  // notify the subscribed GetWorkflow query when the only fields that changed
  // live inside the tasks[] array (assignee, availableActions). Force an
  // explicit cache.modify on TaskType:<id> so the drawer re-renders without
  // waiting for the refetch round-trip.
  const writeAssignmentUpdate = (
    cache: ApolloCache<unknown>,
    updated: AssignmentMutationTaskFragment | null | undefined,
  ) => {
    if (!updated?.id) return;
    cache.modify({
      id: cache.identify({ __typename: "TaskType", id: updated.id }),
      fields: {
        assignee: () => updated.assignee ?? null,
        status: () => updated.status,
        gpxStatus: () => updated.gpxStatus,
        executionState: () => updated.executionState,
        availableActions: () => updated.availableActions,
      },
    });
  };

  // Await the refetch so `loading` stays true through the server round-trip and downstream
  // gates flip atomically with the fresh data (mirrors bulkClaimTasks pattern).
  const assignmentMutationOptions = {
    ...refetchOptions,
    awaitRefetchQueries: Number.isFinite(parsedId),
  };
  const [claimTask, claimResult] = useClaimTaskMutation({
    ...assignmentMutationOptions,
    update: (cache, { data }) => writeAssignmentUpdate(cache, data?.claimTask),
  });
  const [cancelTaskAssignment, cancelAssignmentResult] = useCancelTaskAssignmentMutation({
    ...assignmentMutationOptions,
    update: (cache, { data }) => writeAssignmentUpdate(cache, data?.cancelTaskAssignment),
  });

  const loading =
    startResult.loading ||
    submitResult.loading ||
    approveResult.loading ||
    rejectResult.loading ||
    resolveResult.loading ||
    handoffResult.loading ||
    approveOnBehalfResult.loading ||
    reassignResult.loading ||
    claimResult.loading ||
    cancelAssignmentResult.loading;

  const error =
    startResult.error ||
    submitResult.error ||
    approveResult.error ||
    rejectResult.error ||
    resolveResult.error ||
    handoffResult.error ||
    approveOnBehalfResult.error ||
    reassignResult.error ||
    claimResult.error ||
    cancelAssignmentResult.error ||
    null;

  const performAction = async (
    taskStepId: number,
    action: string,
    payload?: Record<string, unknown>,
  ) => {
    // Action strings here must match what BE's _compute_available_actions emits.
    // See control-tower/control_tower/api/gql/workflows.py:_compute_available_actions.
    switch (action) {
      case "start_work":
        await startTask({ variables: { taskId: taskStepId } });
        break;

      case "submit_for_review":
        await submitTaskForReview({
          variables: {
            taskId: taskStepId,
            note: (payload as NotePayload | undefined)?.note,
          },
        });
        break;

      // Composite maker intent: a NOT_STARTED maker task only exposes start_work,
      // but the single "Submit for review" button must land the task in IN_REVIEW.
      // Chain start_work → submit_for_review so one click crosses both transitions;
      // start_work alone would leave the task IN_PROGRESS and a second click would
      // re-fire start_work and error (IN_PROGRESS → IN_PROGRESS).
      case "start_and_submit":
        await startTask({ variables: { taskId: taskStepId } });
        await submitTaskForReview({
          variables: {
            taskId: taskStepId,
            note: (payload as NotePayload | undefined)?.note,
          },
        });
        break;

      case "complete":
        await resolveTask({
          variables: {
            taskId: taskStepId,
            resolution: "COMPLETED",
            note: (payload as NotePayload | undefined)?.note,
          },
        });
        break;

      case "skip":
        await resolveTask({
          variables: {
            taskId: taskStepId,
            resolution: "SKIPPED",
            note: (payload as NotePayload | undefined)?.note,
          },
        });
        break;

      case "approve": {
        const approvePayload = payload as ApprovePayload | undefined;
        await approveTask({
          variables: {
            taskId: taskStepId,
            note: approvePayload?.note,
            nextAssigneeId: approvePayload?.nextAssigneeId,
          },
        });
        break;
      }

      case "reject": {
        const rejectPayload = payload as RejectPayload | undefined;
        await rejectTask({
          variables: {
            taskId: taskStepId,
            note: rejectPayload?.note,
            rejectionReason: rejectPayload?.rejectionReason,
          },
        });
        break;
      }

      case "handoff":
        await handoffTaskToExternalParty({
          variables: { taskId: taskStepId },
        });
        break;

      case "approve_on_behalf": {
        // BE requires a non-null note ($note: String! in mutations.federation.gql).
        // Mirror the reassign branch: warn + no-op when caller forgot the payload.
        const onBehalfPayload = payload as ApproveOnBehalfPayload | undefined;
        if (!onBehalfPayload?.note) {
          // eslint-disable-next-line no-console
          console.warn(
            "approve_on_behalf invoked without a note payload — an evidence modal must supply note.",
          );
          return;
        }
        await recordExternalApprovalOnBehalf({
          variables: {
            taskId: taskStepId,
            note: onBehalfPayload.note,
            evidenceFileKeys: onBehalfPayload.evidenceFileKeys,
          },
        });
        break;
      }

      case "claim": {
        // Assigns the task to the current user. displayName is optional — BE
        // resolves the user from session when omitted.
        const claimPayload = payload as ClaimPayload | undefined;
        await claimTask({
          variables: {
            taskId: taskStepId,
            displayName: claimPayload?.displayName,
          },
        });
        break;
      }

      case "reassign": {
        // "Reassign to a different person" — requires a user picker UI to
        // gather newAssigneeId. To simply remove the current assignment
        // without picking a replacement, use the separate "unassign" action.
        const reassignPayload = payload as ReassignPayload | undefined;
        if (!reassignPayload?.newAssigneeId) {
          // eslint-disable-next-line no-console
          console.warn(
            "Reassign action invoked without a payload — a user picker modal must supply newAssigneeId.",
          );
          return;
        }
        await reassignTask({
          variables: {
            taskId: taskStepId,
            newAssigneeId: reassignPayload.newAssigneeId,
            newAssigneeDisplayName: reassignPayload.newAssigneeDisplayName,
            note: reassignPayload.note,
          },
        });
        break;
      }

      case "unassign":
        await cancelTaskAssignment({ variables: { taskId: taskStepId } });
        break;

      default:
        throw new Error(`Unknown task action: ${action}`);
    }
  };

  return { performAction, loading, error: error ?? null };
}
